import APITemplate from '@api/APITemplate'
import store from '@state/store'
import con from '@services/connection'
import { simulador } from '@constants/URL'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import {
  dedupeInFlight,
  isSameScope,
  parseNaNSafe,
  SCOPE_SEPARATOR,
  shouldUseCachedData,
} from '@modules/consolidator/utils/Api/Api.js'

class APIConsolidatorSimulatorResult extends APITemplate {
  constructor() {
    super()

    this.simulatorResultRegistry = {}
  }

  buildScopeKey({ clientId, simulationHash }) {
    return `${clientId}${SCOPE_SEPARATOR}${simulationHash}`
  }

  isCachedScope(scopeKey) {
    return isSameScope(
      store.getters['consolidator/simulatorResultClientId'],
      scopeKey
    )
  }

  storeResult(data, scopeKey) {
    store.dispatch('consolidator/setSimulatorResult', {
      data,
      clientId: scopeKey,
    })
  }

  loadResult({ clientId, simulationHash, forceUpdate = false }) {
    if (!clientId || !simulationHash) {
      return this.buildInvalidResponse()
    }

    const scopeKey = this.buildScopeKey({ clientId, simulationHash })

    if (this.isCachedScope(scopeKey)) {
      if (
        shouldUseCachedData({
          forceUpdate,
          lastUpdate: store.getters['consolidator/simulatorResultLastUpdate'],
          timeoutMinutes: MINUTES_TIMEOUT_REQUESTS,
        })
      ) {
        return this.buildCachedResponse()
      }

      if (store.getters['consolidator/simulatorResultLoading']) {
        return this.buildCachedResponse()
      }
    }

    return dedupeInFlight({
      registry: this.simulatorResultRegistry,
      key: scopeKey,
      run: () => this.fetchResult({ clientId, simulationHash, scopeKey }),
    })
  }

  async fetchResult({ clientId, simulationHash, scopeKey }) {
    store.dispatch('consolidator/setSimulatorResultLoading', true)

    const payload = {
      id_cliente: clientId,
      simulation_hash_code: simulationHash,
    }

    try {
      const raw = await con.post(simulador.sim_data, payload)
      return this.applyResponse({ raw, scopeKey }) || {}
    } finally {
      store.dispatch('consolidator/setSimulatorResultLoading', false)
    }
  }

  buildCachedResponse() {
    const cached = store.getters['consolidator/simulatorResult'] || {}

    return {
      status: 200,
      value: this.clone(cached),
    }
  }

  applyResponse({ raw, scopeKey }) {
    if (!raw) {
      this.storeResult({}, scopeKey)
      return this.buildFailedResponse()
    }

    const cleaned = this.cleanResponse(raw)

    if (!cleaned) {
      return this.applyUncleanedResponse({ raw, scopeKey })
    }

    const status = cleaned.status || raw.status || 500
    const message = cleaned.message || raw.message || ''
    const value = status === 200 ? cleaned.value || {} : {}

    this.storeResult(this.clone(value), scopeKey)

    return {
      status,
      message,
      value: this.clone(value),
    }
  }

  cleanResponse(raw) {
    try {
      const rawString = typeof raw === 'string' ? raw : JSON.stringify(raw)
      return parseNaNSafe(rawString)
    } catch {
      return null
    }
  }

  applyUncleanedResponse({ raw, scopeKey }) {
    if (raw.status !== 200) {
      this.storeResult({}, scopeKey)
      return raw
    }

    const baseValue = raw.value || {}
    this.storeResult(this.clone(baseValue), scopeKey)

    return {
      ...raw,
      value: this.clone(baseValue),
    }
  }

  buildFailedResponse() {
    return {
      status: 500,
      message: 'Request failed.',
      value: {},
    }
  }

  async updateSuggestedAsset({
    clientId,
    simulationHash,
    nomeAtivo,
    comentario,
    tags,
    recomendacao,
    setor,
  }) {
    if (!clientId || !simulationHash || !nomeAtivo) {
      return this.buildInvalidResponse()
    }

    let normalizedTags = []

    if (Array.isArray(tags)) {
      normalizedTags = tags
    } else if (typeof tags === 'string' && tags.trim()) {
      try {
        const parsed = JSON.parse(tags)
        normalizedTags = Array.isArray(parsed) ? parsed : [tags.trim()]
      } catch (e) {
        normalizedTags = [tags.trim()]
      }
    }

    const dados = {
      comentario: comentario || null,
      tags: normalizedTags,
    }

    if (recomendacao !== undefined) {
      dados.recomendacao = recomendacao
    }

    if (setor !== undefined) {
      dados.setor = setor
    }

    const payload = {
      id_cliente: clientId,
      simulation_hash_code: simulationHash,
      nome_ativo: nomeAtivo,
      dados: JSON.stringify(dados),
    }

    const r = await con.put(simulador.ativos_sugeridos_update, payload)

    if (!r) {
      return {
        status: 500,
        message: 'Request failed.',
        value: {},
      }
    }

    return r
  }

  buildInvalidResponse() {
    return {
      status: 400,
      message: 'Missing simulation parameters.',
      value: {},
    }
  }

  clone(value) {
    if (!value) return {}
    try {
      return JSON.parse(JSON.stringify(value))
    } catch (e) {
      return {}
    }
  }
}

const apiConsolidatorSimulatorResult = new APIConsolidatorSimulatorResult()
export { apiConsolidatorSimulatorResult }
